import { getOmanPublicationMerchants } from "./shared/api/client";
import { calculateOmanPriority, normalizeOmanRating } from "./omanPriority";

export const OMAN_PUBLICATION_CACHE_VERSION = "oman-publication-v2";
export const OMAN_PUBLICATION_CACHE_KEY = "wn_merchants_oman_publication_v2";
export const OMAN_PUBLICATION_CACHE_TIME_KEY = "wn_merchants_oman_publication_v2_time";
export const OMAN_PUBLICATION_V2_SOURCE = "mx_oman_published_merchants_read_model_v3";
export const OMAN_PUBLICATION_V2_CACHE_VERSION = "oman-publication-v3-shadow";
export const OMAN_PUBLICATION_V3_SOURCE = "mx_oman_golden_master_read_model_v1";
export const OMAN_PUBLICATION_V3_CACHE_VERSION = "oman-publication-v4-golden";
export const OMAN_PUBLICATION_V2_CACHE_KEY = "wn_merchants_oman_publication_v3_shadow";
export const OMAN_PUBLICATION_V2_CACHE_TIME_KEY = "wn_merchants_oman_publication_v3_shadow_time";
export const OMAN_PUBLICATION_V1_CACHE_KEY = "wn_merchants_oman_publication_v1";
export const OMAN_PUBLICATION_V1_CACHE_TIME_KEY = "wn_merchants_oman_publication_v1_time";
export const LEGACY_OMAN_CACHE_KEY = "wn_merchants_oman";
export const LEGACY_SHARED_CACHE_TIME_KEY = "wn_merchants_time";
export const OMAN_PUBLICATION_SOURCE = "mx_muscat_publication_candidates";
export const OMAN_PUBLICATION_VERSIONS = Object.freeze({ v1: "v1", v2: "v2", v3: "v3" });

const PUBLICATION_CONTRACTS = Object.freeze({
  v1: Object.freeze({
    version: "v1",
    source: OMAN_PUBLICATION_SOURCE,
    cacheVersion: OMAN_PUBLICATION_CACHE_VERSION,
    cacheKey: OMAN_PUBLICATION_CACHE_KEY,
    cacheTimeKey: OMAN_PUBLICATION_CACHE_TIME_KEY,
    expectedCount: 2651,
  }),
  v2: Object.freeze({
    version: "v2",
    source: OMAN_PUBLICATION_V2_SOURCE,
    cacheVersion: OMAN_PUBLICATION_V2_CACHE_VERSION,
    cacheKey: OMAN_PUBLICATION_V2_CACHE_KEY,
    cacheTimeKey: OMAN_PUBLICATION_V2_CACHE_TIME_KEY,
    expectedCount: 4444,
  }),
  v3: Object.freeze({
    version: "v3",
    source: OMAN_PUBLICATION_V3_SOURCE,
    cacheVersion: OMAN_PUBLICATION_V3_CACHE_VERSION,
    cacheKey: "wn_merchants_oman_publication_v4_golden",
    cacheTimeKey: "wn_merchants_oman_publication_v4_golden_time",
    expectedCount: 4908,
  }),
});

export function getOmanPublicationVersion(env = typeof process !== "undefined" ? process.env : {}) {
  const version = String(env?.REACT_APP_OMAN_PUBLICATION_VERSION || "v1").trim().toLowerCase();
  if (!Object.values(OMAN_PUBLICATION_VERSIONS).includes(version)) return null;
  return version;
}

function publicationContract(version = getOmanPublicationVersion()) {
  return version ? PUBLICATION_CONTRACTS[version] || null : null;
}

export function clearLegacyOmanCache(storage = localStorage) {
  try {
    storage.removeItem(LEGACY_OMAN_CACHE_KEY);
  } catch {
    /* ignore storage failures */
  }
}

export function readOmanPublicationCache({ storage = localStorage, now = Date.now(), ttlMs, version } = {}) {
  clearLegacyOmanCache(storage);
  const contract = publicationContract(version);
  if (!contract) return null;
  try {
    const cached = storage.getItem(contract.cacheKey);
    const cacheTime = storage.getItem(contract.cacheTimeKey);
    if (!cached || !cacheTime || (now - parseInt(cacheTime, 10)) >= ttlMs) return null;
    const rows = JSON.parse(cached);
    return isValidOmanPublicationRows(rows, { version: contract.version }) ? rows : null;
  } catch {
    return null;
  }
}

export function readStaleOmanPublicationCache(storage = localStorage, { version } = {}) {
  clearLegacyOmanCache(storage);
  const contract = publicationContract(version);
  if (!contract) return null;
  try {
    const cached = storage.getItem(contract.cacheKey);
    const rows = cached ? JSON.parse(cached) : null;
    return isValidOmanPublicationRows(rows, { version: contract.version }) ? rows : null;
  } catch {
    return null;
  }
}

export function writeOmanPublicationCache(rows, { storage = localStorage, now = Date.now(), version } = {}) {
  const contract = publicationContract(version);
  if (!contract || !isValidOmanPublicationRows(rows, { version: contract.version })) return false;
  try {
    storage.setItem(contract.cacheKey, JSON.stringify(rows));
    storage.setItem(contract.cacheTimeKey, String(now));
    return true;
  } catch {
    /* ignore quota failures */
    return false;
  }
}

export async function fetchOmanPublicationMerchants({ token, fetchImpl } = {}) {
  const data = await getOmanPublicationMerchants({ token, fetchImpl });
  const version = data?.publication_version || (data?.source === OMAN_PUBLICATION_SOURCE ? "v1" : null);
  const contract = publicationContract(version);
  if (!contract || data.source !== contract.source || data.cache_version !== contract.cacheVersion) {
    throw new Error("Oman publication endpoint returned an invalid source contract");
  }
  if (data.count !== contract.expectedCount || !Array.isArray(data.rows) || data.rows.length !== contract.expectedCount) {
    throw new Error(`Oman publication endpoint did not return exactly ${contract.expectedCount} rows`);
  }
  const ids = data.rows.map((row) => row.google_place_id || row.id);
  if (new Set(ids).size !== contract.expectedCount) {
    throw new Error("Oman publication endpoint returned duplicate Place IDs");
  }
  const merchants = data.rows.map(mapOmanPublicationRowToNexus);
  return {
    merchants,
    merchant_entities: buildOmanMerchantEntities(merchants),
    diagnostic: {
      source: data.source,
      returned_row_count: data.rows.length,
      cache_version: data.cache_version,
      publication_version: contract.version,
    },
  };
}

export function isValidOmanPublicationRows(rows, { version } = {}) {
  const contract = publicationContract(version);
  if (!contract || !Array.isArray(rows) || rows.length !== contract.expectedCount) return false;
  const ids = rows.map((row) => row && (row.PlaceID || row.google_place_id || row.id));
  if (ids.some((id) => !id)) return false;
  if (new Set(ids).size !== contract.expectedCount) return false;
  return rows.every((row) => {
    if (!row) return false;
    const source = row.Source || row.source;
    const city = row.City || row.city;
    return source === contract.source && (contract.version === "v3" || String(city || "").toLowerCase() === "muscat");
  });
}

export function mapOmanPublicationRowToNexus(row) {
  const openingHours = clean(row.opening_hours);
  const rating = numberOrZero(row.rating);
  const reviews = integerOrZero(row.reviews_count);
  const publicationVersion = clean(row.publication_version);
  const priority = ["v2", "v3"].includes(publicationVersion)
    ? calculateOmanPriority({ rating, user_rating_count: reviews })
    : clean(row.priority) || "Needs Review";
  return {
    id: row.id || row.google_place_id,
    PlaceID: row.google_place_id || row.id,
    Merchant: clean(row.merchant_name),
    Mall: clean(row.location_type) === "mall" ? clean(row.mall_name || row.location_label) : "",
    Area: clean(row.commercial_district) || (["area", "street"].includes(clean(row.location_type)) ? clean(row.location_label) : ""),
    LocationLabel: clean(row.location_label),
    LocationType: clean(row.location_type),
    NearbyMall: clean(row.nearby_mall_name),
    FormattedAddress: clean(row.formatted_address),
    google_maps_url: clean(row.google_maps_url),
    raw_opening_hours: row.regular_opening_hours || row.current_opening_hours || null,
    weekly_opening_hours: row.weekly_opening_hours || {},
    City: clean(row.city) || "Muscat",
    Priority: priority,
    Rating: rating,
    RatingDisplay: formatOmanRating(rating),
    Reviews: reviews,
    AvgPrice: clean(row.price_range),
    Branches: row.branch_count === null || row.branch_count === undefined ? 1 : integerOrZero(row.branch_count),
    Phone: clean(row.phone),
    Website: clean(row.website),
    Reviews3: "",
    Category: clean(row.category) || "Uncategorized",
    OpeningHours: openingHours,
    HoursCategory: hoursCategory(openingHours),
    SubCategory: clean(row.sub_category) || "General",
    BrandGroup: clean(row.brand_group_name),
    brand_group_id: clean(row.brand_group_id),
    brand_group_name: clean(row.brand_group_name),
    Source: clean(row.source) || OMAN_PUBLICATION_SOURCE,
    publication_version: publicationVersion,
    entity_type: clean(row.entity_type),
    branch_id: clean(row.branch_id),
    source_record_id: clean(row.source_record_id),
    branch_resolution_state: clean(row.branch_resolution_state),
    resolution_state: clean(row.resolution_state),
    branch_evidence: clean(row.branch_evidence),
    address: clean(row.address || row.formatted_address),
    lat: row.lat ?? row.latitude ?? null,
    lng: row.lng ?? row.longitude ?? null,
  };
}

/**
 * Build the UI's merchant/entity projection without changing the physical
 * branch rows returned by the API. Only the audited V2 auto-linked state can
 * create a grouped entity; names and aliases are never used as a join key.
 */
export function buildOmanMerchantEntities(branchRows) {
  if (!Array.isArray(branchRows)) throw new Error("Oman branch rows must be an array");

  const seenPlaceIds = new Set();
  const groups = new Map();
  for (const row of branchRows) {
    const placeId = clean(row?.PlaceID || row?.google_place_id || row?.id);
    if (!placeId) throw new Error("Oman branch row is missing google_place_id");
    if (seenPlaceIds.has(placeId)) throw new Error(`Duplicate Oman google_place_id: ${placeId}`);
    seenPlaceIds.add(placeId);

    const brandGroupId = clean(row.brand_group_id);
    const canGroup = Boolean([OMAN_PUBLICATION_V2_SOURCE, OMAN_PUBLICATION_V3_SOURCE].includes(row.Source)
      && ["v2", "v3"].includes(clean(row.publication_version))
      && brandGroupId
      // The public V2 mapper intentionally exposes the audited group id and
      // branch state, but not every internal brand column. If optional state
      // fields are present, they must agree; their absence must not turn a
      // valid API group into a standalone row.
      && (!clean(row.entity_type) || clean(row.entity_type) === "brand")
      && (!clean(row.resolution_state) || clean(row.resolution_state) === "auto_linked")
      && (clean(row.branch_resolution_state) === "auto_linked"
        || (clean(row.publication_version) === "v3" && clean(row.branch_resolution_state) === "golden_master_verified")));
    let entityId = canGroup ? brandGroupId : placeId;
    let group = groups.get(entityId);
    // A stable group id must never mix an audited grouped row with a row that
    // is standalone/manual-review. If the source is inconsistent, fail closed
    // by giving this row its physical identity.
    if (group && group.canGroup !== canGroup) {
      entityId = placeId;
      group = groups.get(entityId);
    }
    group = group || { entityId, canGroup, rows: [] };
    group.rows.push(row);
    groups.set(entityId, group);
  }

  return Array.from(groups.values())
    .sort((a, b) => a.entityId.localeCompare(b.entityId))
    .map(({ entityId, canGroup, rows }) => {
      const branches = [...rows].sort((a, b) => {
        const aId = clean(a.PlaceID || a.google_place_id || a.id);
        const bId = clean(b.PlaceID || b.google_place_id || b.id);
        return aId.localeCompare(bId);
      });
      const primary = branches[0];
      const branchCount = branches.length;
      const brandGroupId = canGroup ? clean(primary.brand_group_id) : "";
      const brandGroupName = canGroup ? clean(primary.brand_group_name || primary.BrandGroup || primary.Merchant) : "";
      const metrics = canGroup
        ? aggregateOmanEntityMetrics(branches)
        : { rating: primary.Rating, reviews: primary.Reviews, priority: primary.Priority };
      return {
        ...primary,
        id: canGroup ? entityId : primary.id,
        PlaceID: canGroup ? "" : primary.PlaceID,
        Merchant: canGroup ? brandGroupName : primary.Merchant,
        OriginalMerchant: canGroup ? brandGroupName : primary.Merchant,
        BrandGroup: brandGroupName,
        brand_group_id: brandGroupId,
        brand_group_name: brandGroupName,
        entity_id: entityId,
        entity_type: canGroup ? "brand" : "merchant",
        branch_count: branchCount,
        Branches: branchCount,
        Priority: metrics.priority,
        Rating: metrics.rating,
        RatingDisplay: formatOmanRating(metrics.rating),
        Reviews: metrics.reviews,
        branches,
        primary_branch: primary,
        grouped: canGroup,
      };
    });
}

export function aggregateOmanEntityMetrics(branches) {
  const rows = Array.isArray(branches) ? branches : [];
  let reviews = 0;
  let weightedRatingTotal = 0;
  let weightedReviewCount = 0;
  for (const branch of rows) {
    const branchReviews = integerOrZero(branch?.Reviews ?? branch?.reviews_count);
    const branchRating = Number(branch?.Rating ?? branch?.rating);
    reviews += branchReviews;
    if (branchReviews > 0 && Number.isFinite(branchRating)) {
      weightedRatingTotal += branchRating * branchReviews;
      weightedReviewCount += branchReviews;
    }
  }
  const rawRating = weightedReviewCount > 0 ? weightedRatingTotal / weightedReviewCount : 0;
  const rating = normalizeOmanRating(rawRating);
  return {
    rating,
    reviews,
    priority: calculateOmanPriority({ rating, user_rating_count: reviews }),
  };
}

/** Preserve numeric precision for calculations while giving Oman views one
 * consistent user-facing rating format. */
export function formatOmanRating(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number.toFixed(1) : "—";
}

export function countOmanPhysicalBranches(branchRows) {
  if (!Array.isArray(branchRows)) return 0;
  const ids = branchRows.map((row) => clean(row?.PlaceID || row?.google_place_id || row?.id));
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) {
    throw new Error("Oman physical branch rows must have unique Place IDs");
  }
  return ids.length;
}

export function searchOmanMerchantEntities(entities, query) {
  const q = clean(query).toLowerCase();
  if (!q) return [];
  return (Array.isArray(entities) ? entities : []).flatMap((entity) => {
    const branches = Array.isArray(entity.branches) ? entity.branches : [entity];
    const matches = branches.filter((branch) => [
      entity.Merchant,
      entity.brand_group_name,
      branch.Merchant,
      branch.Mall,
      branch.NearbyMall,
      branch.Area,
      branch.LocationLabel,
      branch.City,
      branch.FormattedAddress,
      branch.address,
      branch.LocationType,
      branch.PlaceID,
    ].some((value) => clean(value).toLowerCase().includes(q)));
    return matches.length ? [{ ...entity, matched_branches: matches }] : [];
  });
}

function clean(value) {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  return ["none", "null", "nan"].includes(s.toLowerCase()) ? "" : s;
}

function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function integerOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function hoursCategory(value) {
  const s = clean(value).toLowerCase();
  if (!s) return "Not Available";
  if (s.includes("24 hours") || s.includes("24/7") || s.includes("٢٤ ساعة")) return "24 Hours";
  return "Specified Hours";
}
